import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { AdminService } from '@/services/admin.service';
import type { FormField, FormSection } from '@/components/admin/FormFieldEditor';

export interface RequirementSortOrder {
  id: string;
  displayOrder: number;
}

export interface SaveFormConfigurationInput {
  controlTypeId: string;
  formConfiguration: {
    fields: FormField[];
    sections?: FormSection[];
  };
  /** Requirement order from the Sortering tab; a DB column, so it needs its own write. */
  requirementSortOrders: RequirementSortOrder[];
}

/**
 * Saves the admin form builder as one unit: the formConfiguration blob first, then
 * the requirement sort orders, and only then the cache invalidation.
 *
 * The two writes used to be split across the page and the builder, with the list
 * refetch fired after the first one. The refetch then raced the sort-order write
 * and the admin saw the old requirement order until a manual reload.
 */
export const useSaveFormConfiguration = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({
      controlTypeId,
      formConfiguration,
      requirementSortOrders,
    }: SaveFormConfigurationInput) => {
      await AdminService.updateControlType(controlTypeId, { formConfiguration });
      await AdminService.updateRequirementSortOrders(controlTypeId, requirementSortOrders);
    },
    onSuccess: () => {
      enqueueSnackbar('Formulärkonfiguration sparad', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'control-types'] });
    },
    onError: (error: any) => {
      enqueueSnackbar(error?.message || 'Fel vid uppdatering av formulärkonfiguration', {
        variant: 'error',
      });
    },
  });
};
